<template>
  <AuthLayout :max-width="480">
    <template v-if="loading">
      <div class="text-center">
        <v-progress-circular indeterminate color="primary" class="mb-4" />
        <div>Link ellenőrzése...</div>
      </div>
    </template>

    <template v-else-if="tokenError">
      <div class="text-center">
        <v-icon color="error" size="56" class="mb-4">mdi-alert-circle</v-icon>
        <v-card-title class="justify-center mb-2"
          >Érvénytelen link</v-card-title
        >
        <v-card-text>
          A jelszó visszaállító link érvénytelen vagy lejárt. Kérjen újat a
          bejelentkezési oldalon.
        </v-card-text>
        <v-btn color="primary" variant="tonal" to="/forgot-password"
          >Új link kérése</v-btn
        >
      </div>
    </template>

    <template v-else>
      <v-card-title class="mb-2">Új jelszó megadása</v-card-title>
      <v-form ref="formRef" @submit.prevent="submit">
        <v-card-text>
          <v-text-field
            v-model="password"
            label="Új jelszó"
            :append-inner-icon="visible ? 'mdi-eye-off' : 'mdi-eye'"
            :type="visible ? 'text' : 'password'"
            density="compact"
            variant="outlined"
            prepend-inner-icon="mdi-lock-outline"
            class="mb-2"
            autofocus
            :rules="[rules.required, rules.minPassword]"
            @click:append-inner="visible = !visible"
          />
          <v-text-field
            v-model="passwordConfirm"
            label="Új jelszó megerősítése"
            :type="visible ? 'text' : 'password'"
            density="compact"
            variant="outlined"
            prepend-inner-icon="mdi-lock-outline"
            :rules="[rules.required, rules.passwordMatch]"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn type="submit" color="primary" :loading="submitting"
            >Jelszó mentése</v-btn
          >
        </v-card-actions>
      </v-form>
    </template>
  </AuthLayout>
</template>

<script setup lang="ts">
import AuthLayout from "@/components/layout/AuthLayout.vue";
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { PasswordResetService } from "@/services/PasswordResetService";
import { useToast } from "@/services/useToast";

const route = useRoute();
const router = useRouter();
const passwordResetService = new PasswordResetService();
const { showToast } = useToast();

const token = ref("");
const password = ref("");
const passwordConfirm = ref("");
const visible = ref(false);
const loading = ref(true);
const tokenError = ref(false);
const submitting = ref(false);
const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(
  null
);

const rules = {
  required: (v: string) => !!v?.trim() || "Kötelező mező",
  minPassword: (v: string) =>
    v?.length >= 8 || "A jelszónak legalább 8 karakter hosszúnak kell lennie",
  passwordMatch: (v: string) =>
    v === password.value || "A jelszavak nem egyeznek",
};

onMounted(async () => {
  token.value = (route.query.token as string) ?? "";
  if (!token.value) {
    tokenError.value = true;
    loading.value = false;
    return;
  }
  try {
    await passwordResetService.validateToken(token.value);
  } catch {
    tokenError.value = true;
  } finally {
    loading.value = false;
  }
});

async function submit() {
  const { valid } = await formRef.value!.validate();
  if (!valid) return;

  submitting.value = true;
  try {
    const { role } = await passwordResetService.confirmReset(
      token.value,
      password.value
    );
    showToast("A jelszót sikeresen megváltoztattuk");
    router.push(role === "ADMIN" ? "/admin/login" : "/login");
  } catch (e: any) {
    const status = e?.response?.status;
    if (status === 400) {
      // The token expired or was already used between page load and submit.
      tokenError.value = true;
    } else if (status === 422) {
      showToast("A megadott jelszó nem felel meg a követelményeknek", "error");
    } else {
      showToast("A jelszó mentése sikertelen. Kérjük, próbálja újra.", "error");
    }
  } finally {
    submitting.value = false;
  }
}
</script>
